import { useEffect, useMemo, useState } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Fab,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";
import { getUnreadNotificationCount } from "../api/notifications";
import AIAssistantDrawer from "../components/AIAssistantDrawer";
import Sidebar from "../components/Sidebar";
import LanguageSelector from "../components/LanguageSelector";
import WorkspaceSwitcher from "../components/WorkspaceSwitcher";
import { useAIAssistant } from "../context/AIAssistantContext";
import { useAuth } from "../context/AuthContext";
import { useConnectivity } from "../context/ConnectivityContext";
import { titleCase } from "../utils/format";

const drawerWidth = 280;

const PAGE_LABELS = {
  "/dashboard": "Dashboard",
  "/products": "Products",
  "/pos": "Point of Sale",
  "/hardware": "Hardware setup",
  "/sales": "Sales",
  "/inventory": "Inventory",
  "/customers": "Customers",
  "/conversations": "Conversations",
  "/notifications": "Notifications",
  "/employee-operations": "Team inbox",
  "/quotations": "Quotations",
  "/pricing": "Pricing & Discounts",
  "/documents": "Knowledge documents",
  "/whatsapp": "WhatsApp",
  "/pos-integrations": "POS connector",
  "/audit-logs": "Audit logs",
  "/store-import": "Import store data",
  "/store-profile": "Store profile",
  "/settings": "Settings & security",
  "/team": "Team members",
  "/purchase-invoices": "Purchase invoices",
  "/purchase-orders": "Purchase orders",
  "/onboarding": "Approval status",
  "/platform": "Platform operations",
  "/platform/ai-usage": "AI usage & cost",
};
const PAGE_KEYS = {
  "/dashboard": "dashboard", "/products": "products", "/pos": "pos",
  "/hardware": "hardware", "/sales": "sales", "/inventory": "inventory",
  "/purchase-invoices": "purchaseInvoices", "/payments-expenses": "paymentsExpenses",
  "/purchase-orders": "purchaseOrders",
  "/pricing": "pricing", "/reports": "reports", "/team": "team",
  "/customers": "customers", "/conversations": "conversations",
  "/notifications": "notifications", "/employee-operations": "teamInbox",
  "/quotations": "quotations", "/documents": "documents", "/store-profile": "storeProfile",
  "/settings": "settings", "/store-import": "import", "/audit-logs": "auditLogs",
  "/onboarding": "approvalStatus", "/pos-integrations": "integrations",
};

export default function MainLayout({ children }) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.resolvedLanguage === "ar";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [utilityAnchor, setUtilityAnchor] = useState(null);
  const isTinyScreen = useMediaQuery("(max-width:379px)");
  const { state: connectivityState, checkNow: checkConnectivity } = useConnectivity();
  const {
    user,
    role,
    platformRole,
    contextReady,
    workspaceOperational,
    selectedWorkspace,
    selectedStore,
    offlineAuthActive,
    logout,
  } = useAuth();
  const { open: assistantOpen, openAssistant } = useAIAssistant();
  const location = useLocation();
  const navigate = useNavigate();
  const pagePath = location.pathname.startsWith("/purchase-orders/")
    ? "/purchase-orders"
    : location.pathname;
  const pageTitle = PAGE_KEYS[pagePath]
    ? t(`nav.${PAGE_KEYS[pagePath]}`)
    : PAGE_LABELS[pagePath] || "AI Business OS";

  const isPlatformPage = location.pathname === "/platform"
    || location.pathname.startsWith("/platform/");
  const hasStoreExperience = contextReady && workspaceOperational && !isPlatformPage;
  const canManageStore = hasStoreExperience && ["owner", "admin"].includes(role);
  const onlineUtilities = connectivityState === "online";
  const canOpenWhatsApp = canManageStore && onlineUtilities;
  const canOpenStoreProfile = canManageStore && onlineUtilities;
  const canOpenSettings = !isPlatformPage && onlineUtilities;
  const showTenantUtilities = onlineUtilities && (hasStoreExperience || canOpenSettings);
  const accountLabel = titleCase(platformRole || role || "account");
  const accountInitial = (user?.username || user?.email || "U").slice(0, 1).toUpperCase();
  const accountMenuOpen = Boolean(accountAnchor);
  const utilityMenuOpen = Boolean(utilityAnchor);

  const legacyAssistantOpenRequested = Boolean(location.state?.openAssistant);

  useEffect(() => {
    if (!hasStoreExperience || !legacyAssistantOpenRequested) return;
    openAssistant();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [
    hasStoreExperience,
    legacyAssistantOpenRequested,
    location.pathname,
    location.search,
    navigate,
    openAssistant,
  ]);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const loadNotificationCount = async () => {
      if (!hasStoreExperience || connectivityState !== "online") {
        if (!cancelled) setNotificationCount(0);
        return;
      }
      try {
        const response = await getUnreadNotificationCount();
        if (!cancelled) {
          setNotificationCount(Number(response.data?.unread_count || 0));
        }
      } catch {
        // Header navigation must remain usable if notification count is temporarily unavailable.
      }
    };

    loadNotificationCount();
    if (hasStoreExperience) {
      timer = window.setInterval(loadNotificationCount, 30000);
    }

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
    };
  }, [
    connectivityState,
    hasStoreExperience,
    location.pathname,
    selectedStore?.public_id,
    selectedWorkspace?.public_id,
  ]);

  const unreadLabel = useMemo(() => {
    if (notificationCount <= 0) return t("nav.openNotifications");
    return t("nav.unreadNotifications", { count: notificationCount });
  }, [notificationCount, t]);

  const closeAccountMenu = () => setAccountAnchor(null);
  const closeUtilityMenu = () => setUtilityAnchor(null);

  const navigateFromUtilityMenu = (path) => {
    closeUtilityMenu();
    navigate(path);
  };

  const handleLogout = async () => {
    closeAccountMenu();
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
        backgroundImage:
          "radial-gradient(circle at 90% 0%, rgba(37,99,235,0.07), transparent 28%), radial-gradient(circle at 25% 100%, rgba(124,58,237,0.055), transparent 32%)",
      }}
    >
      <Sidebar
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          ml: { md: `${drawerWidth}px` },
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: "rgba(246,248,252,0.9)",
          color: "text.primary",
          borderBottom: "1px solid rgba(226,232,240,0.9)",
          backdropFilter: "blur(18px)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: 72,
            px: { xs: 1.5, sm: 2.5, md: 4 },
            gap: 1.5,
            "@media (max-width:379px)": { px: 0.75, gap: 0.5 },
          }}
        >
          <IconButton
            onClick={() => setMobileOpen(true)}
            aria-label={t("nav.openNavigation")}
            sx={{
              display: { xs: "inline-flex", md: "none" },
              width: 42,
              height: 42,
              "@media (max-width:379px)": { width: 36, height: 36 },
              borderRadius: 2.5,
              bgcolor: "#fff",
              border: "1px solid #e2e8f0",
            }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Box sx={{ display: { xs: "none", md: "block" }, flexGrow: 1, minWidth: 0 }}>
            {contextReady ? (
              <WorkspaceSwitcher />
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>
                  PLATFORM
                </Typography>
                <Typography fontWeight={900}>{pageTitle}</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: { xs: "block", md: "none" }, flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={750} noWrap display="block">
              AI Business OS
            </Typography>
            <Typography fontWeight={900} noWrap>
              {pageTitle}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={{ xs: 0.75, sm: 1.1 }}
            alignItems="center"
            sx={{ "@media (max-width:379px)": { gap: 0.25 } }}
          >
            {showTenantUtilities && (
              <Box
                role="group"
                aria-label={t("nav.utilityActions")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0, sm: 0.15 },
                }}
              >
                {canOpenWhatsApp && (
                  <Tooltip title="WhatsApp">
                    <IconButton
                      aria-label={t("nav.openWhatsApp")}
                      onClick={() => navigate("/whatsapp")}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        "@media (max-width:379px)": { width: 34, height: 34 },
                        borderRadius: 2,
                        color: "#16a34a",
                        bgcolor: location.pathname === "/whatsapp"
                          ? "rgba(34,197,94,0.10)"
                          : "transparent",
                        "&:hover": { bgcolor: "rgba(34,197,94,0.08)" },
                      }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {hasStoreExperience && (
                  <Tooltip title={t("nav.notifications")}>
                    <IconButton
                      aria-label={unreadLabel}
                      onClick={() => navigate("/notifications")}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        "@media (max-width:379px)": { width: 34, height: 34 },
                        borderRadius: 2,
                        color: location.pathname === "/notifications" ? "primary.main" : "#475569",
                        bgcolor: location.pathname === "/notifications"
                          ? "rgba(37,99,235,0.08)"
                          : "transparent",
                        "&:hover": { bgcolor: "rgba(15,23,42,0.055)" },
                      }}
                    >
                      <Badge
                        badgeContent={notificationCount}
                        max={99}
                        color="warning"
                        invisible={notificationCount <= 0}
                        overlap="circular"
                      >
                        <NotificationsRoundedIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}

                {!isTinyScreen && canOpenStoreProfile && (
                  <Tooltip title={t("nav.storeProfile")}>
                    <IconButton
                      aria-label={t("nav.openStoreProfile")}
                      onClick={() => navigate("/store-profile")}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        borderRadius: 2,
                        color: location.pathname === "/store-profile" ? "primary.main" : "#475569",
                        bgcolor: location.pathname === "/store-profile"
                          ? "rgba(37,99,235,0.08)"
                          : "transparent",
                        "&:hover": { bgcolor: "rgba(15,23,42,0.055)" },
                      }}
                    >
                      <StorefrontRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {!isTinyScreen && canOpenSettings && (
                  <Tooltip title={t("nav.settings")}>
                    <IconButton
                      aria-label={t("nav.openSettings")}
                      onClick={() => navigate("/settings")}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        borderRadius: 2,
                        color: location.pathname === "/settings" ? "primary.main" : "#475569",
                        bgcolor: location.pathname === "/settings"
                          ? "rgba(37,99,235,0.08)"
                          : "transparent",
                        "&:hover": { bgcolor: "rgba(15,23,42,0.055)" },
                      }}
                    >
                      <SettingsRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {isTinyScreen && (canOpenStoreProfile || canOpenSettings) && (
                  <Tooltip title={t("nav.moreActions")}>
                    <IconButton
                      aria-label={t("nav.moreActions")}
                      aria-controls={utilityMenuOpen ? "store-utility-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={utilityMenuOpen ? "true" : undefined}
                      onClick={(event) => setUtilityAnchor(event.currentTarget)}
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 2,
                        color: ["/store-profile", "/settings"].includes(location.pathname)
                          ? "primary.main"
                          : "#475569",
                        bgcolor: ["/store-profile", "/settings"].includes(location.pathname)
                          ? "rgba(37,99,235,0.08)"
                          : "transparent",
                        "&:hover": { bgcolor: "rgba(15,23,42,0.055)" },
                      }}
                    >
                      <MoreHorizRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}

            <Button
              aria-label={t("nav.openAccount")}
              aria-controls={accountMenuOpen ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={accountMenuOpen ? "true" : undefined}
              onClick={(event) => setAccountAnchor(event.currentTarget)}
              disableRipple
              sx={{
                minWidth: 0,
                height: 44,
                px: { xs: 0.4, sm: 0.7 },
                pr: { xs: 0.4, sm: 1.05 },
                gap: 0.75,
                "@media (max-width:379px)": { height: 38, px: 0.2, pr: 0.2 },
                borderRadius: 2.75,
                border: "1px solid transparent",
                color: "text.primary",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.8)",
                  borderColor: "#e2e8f0",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: platformRole ? "warning.main" : "primary.main",
                  fontWeight: 900,
                  fontSize: "0.9rem",
                  "@media (max-width:379px)": { width: 30, height: 30, fontSize: "0.8rem" },
                  boxShadow: "0 6px 16px rgba(37,99,235,0.18)",
                }}
              >
                {accountInitial}
              </Avatar>
              <Typography
                variant="body2"
                fontWeight={850}
                sx={{ display: { xs: "none", sm: "block" }, whiteSpace: "nowrap" }}
              >
                {accountLabel}
              </Typography>
              <KeyboardArrowDownRoundedIcon
                fontSize="small"
                sx={{ display: { xs: "none", sm: "block" }, color: "text.secondary" }}
              />
            </Button>
          </Stack>
        </Toolbar>

        {contextReady && (
          <Box sx={{ display: { xs: "block", md: "none" }, px: 1.5, pb: 1.5 }}>
            <WorkspaceSwitcher compact />
          </Box>
        )}
      </AppBar>

      <Menu
        id="store-utility-menu"
        anchorEl={utilityAnchor}
        open={utilityMenuOpen}
        onClose={closeUtilityMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: isArabic ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: isArabic ? "left" : "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 210,
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              boxShadow: "0 18px 44px rgba(15,23,42,0.14)",
            },
          },
        }}
      >
        {canOpenStoreProfile && (
          <MenuItem onClick={() => navigateFromUtilityMenu("/store-profile")}>
            <ListItemIcon>
              <StorefrontRoundedIcon fontSize="small" />
            </ListItemIcon>
            {t("nav.storeProfile")}
          </MenuItem>
        )}
        {canOpenSettings && (
          <MenuItem onClick={() => navigateFromUtilityMenu("/settings")}>
            <ListItemIcon>
              <SettingsRoundedIcon fontSize="small" />
            </ListItemIcon>
            {t("nav.settings")}
          </MenuItem>
        )}
      </Menu>

      <Menu
        id="account-menu"
        anchorEl={accountAnchor}
        open={accountMenuOpen}
        onClose={closeAccountMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: isArabic ? "left" : "right" }}
        transformOrigin={{ vertical: "top", horizontal: isArabic ? "left" : "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 220,
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              boxShadow: "0 18px 44px rgba(15,23,42,0.14)",
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.2, maxWidth: 260 }}>
          <Typography variant="body2" fontWeight={900} noWrap>
            {user?.username || t("common.account")}
          </Typography>
          {user?.email && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {user.email}
            </Typography>
          )}
        </Box>
        <Divider />
        {!isPlatformPage && (
          <Box sx={{ px: 2, py: 1.5 }}><LanguageSelector sx={{ width: "100%" }} /></Box>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          {t("common.signOut")}
        </MenuItem>
      </Menu>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          p: { xs: 2, sm: 2.75, md: 4 },
          pt: contextReady
            ? { xs: 18, sm: 18, md: 13 }
            : { xs: 12, sm: 12, md: 13 },
          overflowX: "hidden",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1520, mx: "auto" }}>
          {connectivityState !== "online" && connectivityState !== "checking" && (
            <Alert
              severity={connectivityState === "degraded" ? "warning" : "info"}
              sx={{ mb: 2.25, borderRadius: 3 }}
              action={
                <Button color="inherit" size="small" onClick={() => checkConnectivity({ reconnecting: true })}>
                  {t("common.retry")}
                </Button>
              }
            >
              {offlineAuthActive
                ? t("nav.continuity")
                : connectivityState === "reconnecting"
                  ? t("nav.reconnecting")
                  : t("nav.offline")}
            </Alert>
          )}
          {children}
        </Box>
      </Box>

      {hasStoreExperience && connectivityState === "online" && (
        <>
          {!assistantOpen && (
            <Tooltip title={t("nav.askAi")} placement={isArabic ? "right" : "left"}>
              <Fab
                color="primary"
                aria-label={t("nav.openAi")}
                onClick={openAssistant}
                sx={{
                  position: "fixed",
                  insetInlineEnd: { xs: 18, sm: 28 },
                  bottom: { xs: 18, sm: 28 },
                  width: { xs: 54, sm: 60 },
                  height: { xs: 54, sm: 60 },
                  zIndex: (theme) => theme.zIndex.appBar + 1,
                  fontSize: { xs: 25, sm: 28 },
                  boxShadow: "0 14px 32px rgba(37,99,235,0.32)",
                }}
              >
                <Box component="span" aria-hidden="true">🤖</Box>
              </Fab>
            </Tooltip>
          )}
          <AIAssistantDrawer />
        </>
      )}
    </Box>
  );
}
